import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CreditCard, Smartphone } from 'lucide-react'
import { useDonationPayment } from '../contexts/DonationPaymentContext'
import LoadingSpinner from '../../secret/components/ui/LoadingSpinner'

interface DonationPaymentFormProps {
  containerId: string
  onPaymentComplete: () => void
  onApplePayComplete?: () => void
  onGooglePayComplete?: () => void
  isProcessing: boolean
  buttonText: string
}

/**
 * DONATION PAYMENT FORM
 *
 * A simplified payment form for the donation flow.
 * Uses DonationPaymentContext (no auth required).
 */
const DonationPaymentForm: React.FC<DonationPaymentFormProps> = ({
  containerId,
  onPaymentComplete,
  onApplePayComplete,
  onGooglePayComplete,
  isProcessing,
  buttonText,
}) => {
  const {
    paymentError,
    clearError,
    isApplePayAvailable,
    isGooglePayAvailable,
  } = useDonationPayment()

  const [selectedMethod, setSelectedMethod] = useState<'card' | 'apple' | 'google'>('card')
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  // Clear errors when payment method changes
  useEffect(() => {
    clearError()
  }, [selectedMethod, clearError])

  // Handle payment submission
  const handlePayment = async () => {
    clearError()

    switch (selectedMethod) {
      case 'apple':
        onApplePayComplete?.()
        break
      case 'google':
        onGooglePayComplete?.()
        break
      default:
        onPaymentComplete()
    }
  }

  return (
    <div>
      {/* Payment Method Selection */}
      <div className="space-y-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Method</h3>

        <div className="grid gap-3">
          {/* Card Payment */}
          <button
            onClick={() => setSelectedMethod('card')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              selectedMethod === 'card'
                ? 'border-[#8B3A3A] bg-gray-50 dark:bg-gray-700'
                : 'border-gray-300 dark:border-gray-600 hover:border-[#8B3A3A]'
            }`}
          >
            <div className="flex items-center">
              <CreditCard className="w-5 h-5 mr-3 text-[#8B3A3A]" />
              <span className="text-gray-900 dark:text-white font-bold text-lg">Credit or Debit Card</span>
            </div>
          </button>

          {/* Apple Pay */}
          {isApplePayAvailable ? (
            <button
              onClick={() => setSelectedMethod('apple')}
              className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                selectedMethod === 'apple'
                  ? 'border-[#8B3A3A] bg-gray-50 dark:bg-gray-700'
                  : 'border-gray-300 dark:border-gray-600 hover:border-[#8B3A3A]'
              }`}
            >
              <Smartphone className="w-5 h-5 mr-3 text-[#8B3A3A]" />
              <span className="text-gray-900 dark:text-white font-medium">Apple Pay</span>
            </button>
          ) : (
            <div className="flex items-center p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed">
              <Smartphone className="w-5 h-5 mr-3 text-gray-400 dark:text-gray-600" />
              <span className="text-gray-400 dark:text-gray-600 font-medium">Apple Pay (Unavailable)</span>
            </div>
          )}

          {/* Google Pay */}
          {isGooglePayAvailable ? (
            <button
              onClick={() => setSelectedMethod('google')}
              className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                selectedMethod === 'google'
                  ? 'border-[#8B3A3A] bg-gray-50 dark:bg-gray-700'
                  : 'border-gray-300 dark:border-gray-600 hover:border-[#8B3A3A]'
              }`}
            >
              <Smartphone className="w-5 h-5 mr-3 text-[#8B3A3A]" />
              <span className="text-gray-900 dark:text-white font-medium">Google Pay</span>
            </button>
          ) : (
            <div className="flex items-center p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed">
              <Smartphone className="w-5 h-5 mr-3 text-gray-400 dark:text-gray-600" />
              <span className="text-gray-400 dark:text-gray-600 font-medium">Google Pay (Unavailable)</span>
            </div>
          )}
        </div>
      </div>

      {/* Card Form Container - Square SDK attaches here */}
      {/* Always render but hide when not selected to preserve Square card element */}
      <div className={`space-y-2 mb-6 ${selectedMethod === 'card' ? '' : 'hidden'}`}>
        <h4 className="text-md font-medium text-gray-900 dark:text-white">Card Information</h4>
        <div
          id={containerId}
          className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-900 min-h-[120px]"
        />
      </div>

      {/* Terms of Donation Agreement */}
      <div className="mb-6">
        <div className="flex items-start space-x-3">
          <div className="relative flex items-center justify-center mt-0.5">
            <div
              onClick={() => setAgreedToTerms(!agreedToTerms)}
              className="w-5 h-5 border-2 rounded transition-all flex items-center justify-center cursor-pointer"
              style={{
                backgroundColor: agreedToTerms ? '#8B3A3A' : '#ffffff',
                borderColor: agreedToTerms ? '#8B3A3A' : '#d1d5db',
                borderWidth: '2px'
              }}
            >
              {agreedToTerms && (
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <label
            className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed cursor-pointer"
            onClick={() => setAgreedToTerms(!agreedToTerms)}
          >
            I agree to the{' '}
            <Link
              to="/terms-of-donation"
              className="text-[#8B3A3A] dark:text-[#DAA520] hover:text-[#6B2A2A] dark:hover:text-[#B8860B] underline font-medium transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Terms of Donation
            </Link>
            , including the non-refund policy.
          </label>
        </div>
      </div>

      {/* Error Display */}
      {paymentError && (
        <div className="bg-red-100 dark:bg-red-950 border-l-4 border-red-500 text-red-700 dark:text-red-300 p-4 mb-4 rounded">
          <p className="text-sm">{paymentError}</p>
        </div>
      )}

      {/* Payment Button */}
      <div className="mt-6 space-y-3">
        <button
          onClick={handlePayment}
          disabled={isProcessing || !agreedToTerms}
          className="w-full bg-[#8B3A3A] hover:bg-[#6B2A2A] !text-white font-bold py-4 px-4 rounded-xl shadow-lg transform hover:-translate-y-0.5 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <LoadingSpinner size="sm" />
              <span className="ml-2 !text-white">Processing...</span>
            </div>
          ) : (
            <span className="!text-white">{buttonText}</span>
          )}
        </button>

        {/* Security Notice */}
        <div className="text-center text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center justify-center space-x-2 mb-1">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="text-gray-700 dark:text-gray-300">Secured by Square</span>
          </div>
          <p>Your payment information is encrypted and secure.</p>
        </div>
      </div>
    </div>
  )
}

export default DonationPaymentForm
